import React, { useState, useEffect, useMemo } from "react";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Search } from "lucide-react";
import { useSortableData, SortConfig } from "./hooks/useSortableData";
import SortableHeaderCell from "./components/SortableHeaderCell";
import SortableTableRow from "./components/SortableTableRow";
import { cn } from "@/lib/utils";
import "@/styles/tableStyles.css";
import { 
  Pagination, 
  PaginationContent, 
  PaginationEllipsis, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface PaginatedTableProps<T> {
  data: T[];
  isLoading?: boolean;
  columns: {
    key: keyof T | 'actions' | string;
    header: string;
    sortable?: boolean;
    width?: string;
    className?: string;
    render?: (item: T) => React.ReactNode;
  }[];
  onRowClick?: (item: T) => void;
  keyExtractor: (item: T) => string;
  selectable?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  noDataText?: string;
  loadingText?: string;
  containerClassName?: string;
  tableClassName?: string;
  children?: React.ReactNode;
  embedded?: boolean;
  renderHeader?: () => React.ReactNode;
  renderBulkActions?: (selectedItems: T[]) => React.ReactNode;
  // Pagination props
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  isPaginationControlled?: boolean;
  // Add searchTerm prop
  searchTerm?: string;
  // Add onSelectionChange prop
  onSelectionChange?: (selectedItems: T[]) => void;
  // Add selectedItems prop for controlled selection
  selectedItems?: T[];
  // Add hidePageSizeSelector prop
  hidePageSizeSelector?: boolean;
  // Add support for shift-click selection
  onRowSelect?: (item: T, index: number, event: React.MouseEvent) => void;
  // Optional extras to render in header row between search and page size
  renderHeaderExtras?: () => React.ReactNode;
  /** When set, search input is controlled and filtering should be handled by the parent (e.g. server-side search). */
  onSearchTermChange?: (term: string) => void;
  /** When true, rows are not filtered by the search box locally (parent loads matching rows). */
  skipLocalRowFilter?: boolean;
  /** Called when Enter is pressed in the search box (for explicit-submit server search). */
  onSearchSubmit?: () => void;
  /** Initial page size when using built-in (non-controlled) pagination. */
  initialLocalPageSize?: number;
}

const PaginatedTable = React.forwardRef<any, PaginatedTableProps<any>>(({
  data, 
  isLoading = false, 
  columns, 
  onRowClick, 
  keyExtractor, 
  selectable = false,
  searchable = false,
  searchPlaceholder = "Search...",
  noDataText = "No data found",
  loadingText = "Loading...",
  containerClassName,
  tableClassName,
  children,
  embedded = false,
  renderHeader,
  renderBulkActions,
  pagination,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 100, 250],
  isPaginationControlled = false,
  searchTerm,
  onSelectionChange,
  selectedItems,
  hidePageSizeSelector = false,
  onRowSelect,
  renderHeaderExtras,
  onSearchTermChange,
  skipLocalRowFilter = false,
  onSearchSubmit,
  initialLocalPageSize
}, ref) => {
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [localSelectedItems, setLocalSelectedItems] = useState<any[]>([]);
  const { sortConfig, handleSort, sortData } = useSortableData<any>(data);

  const isSearchControlled = typeof onSearchTermChange === "function";
  const searchTermProvided = searchTerm !== undefined;
  const activeSearchTerm = isSearchControlled
    ? (searchTerm ?? "")
    : searchTermProvided
      ? String(searchTerm)
      : localSearchTerm;

  const defaultLocalPageSize = initialLocalPageSize ?? 25;

  // Filter data based on search term (skip when parent loads matching rows server-side)
  const filteredData = useMemo(() => {
    if (skipLocalRowFilter) return sortData(data);
    if (!activeSearchTerm) return sortData(data);

    return sortData(
      data.filter((item) =>
        Object.entries(item).some(([_key, value]) => {
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(activeSearchTerm.toLowerCase());
        })
      )
    );
  }, [data, activeSearchTerm, sortData, skipLocalRowFilter]);

  // Local pagination state if not controlled externally
  const [localPagination, setLocalPagination] = useState(() => {
    const ps = defaultLocalPageSize;
    return {
      currentPage: 1,
      pageSize: ps,
      totalItems: data.length,
      totalPages: Math.ceil(data.length / ps) || 1,
      hasNextPage: data.length > ps,
      hasPrevPage: false
    };
  });
  
  // Use either provided pagination or local pagination
  const activePagination = pagination || localPagination;

  // Use either controlled selectedItems or local state
  const activeSelectedItems = selectedItems || localSelectedItems;
  
  // Update local selection when selectedItems prop changes
  useEffect(() => {
    if (selectedItems) {
      setLocalSelectedItems(selectedItems);
    }
  }, [selectedItems]);

  // Reset selected items when data changes
  useEffect(() => {
    setLocalSelectedItems([]);
    
    // Update local pagination if not controlled externally
    if (!isPaginationControlled) {
      const totalFilteredItems = filteredData.length;
      const totalPages = Math.ceil(totalFilteredItems / localPagination.pageSize);
      
      setLocalPagination(prev => ({
        ...prev,
        currentPage: Math.min(prev.currentPage, Math.max(1, totalPages)),
        totalItems: totalFilteredItems,
        totalPages,
        hasNextPage: prev.currentPage < totalPages,
        hasPrevPage: prev.currentPage > 1
      }));
    }
  }, [data, isPaginationControlled, filteredData.length, localPagination.pageSize]);

  // Update local pagination when search term changes
  useEffect(() => {
    if (!isPaginationControlled) {
      const totalFilteredItems = filteredData.length;
      const totalPages = Math.ceil(totalFilteredItems / localPagination.pageSize) || 1;

      setLocalPagination((prev) => ({
        ...prev,
        currentPage: 1, // Reset to first page on search
        totalItems: totalFilteredItems,
        totalPages,
        hasNextPage: 1 < totalPages,
        hasPrevPage: false
      }));
    }
  }, [activeSearchTerm, isPaginationControlled, filteredData.length, localPagination.pageSize]);

  // Call onSelectionChange when selectedItems changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(activeSelectedItems);
    }
  }, [activeSelectedItems, onSelectionChange]);

  const handleSelectAll = (checked: boolean) => {
    const newSelectedItems = checked ? paginatedData : [];
    setLocalSelectedItems(newSelectedItems);
    if (onSelectionChange) {
      onSelectionChange(newSelectedItems);
    }
  };

  const handleSelectItem = (item: any, checked: boolean, event?: React.MouseEvent) => {
    const actualIndex = filteredData.findIndex(i => keyExtractor(i) === keyExtractor(item));
    
    // If onRowSelect is provided, use it for all selection (it handles shift-click and regular clicks)
    if (onRowSelect && actualIndex !== -1 && event) {
      // Pass the checked state so onRowSelect can set selection correctly instead of toggling
      // We need to extend the event or pass checked separately
      // Since onRowSelect signature doesn't include checked, we'll modify the approach
      // For now, call onRowSelect which will handle shift-click, and for regular clicks
      // we'll need to ensure it receives the checked state somehow
      
      // If shift-click, let onRowSelect handle it
      if (event.shiftKey) {
        onRowSelect(item, actualIndex, event);
        return;
      }
      
      // For regular checkbox clicks, we need to pass checked state
      // Since onRowSelect doesn't accept checked, we'll handle it here but still call
      // onRowSelect to update lastClickedIndex. But we need to prevent double-update.
      // Actually, let's modify the approach: always call onRowSelect, but make it
      // accept an optional checked parameter through a custom property on the event
      const eventWithChecked = { ...event, __checked: checked } as React.MouseEvent & { __checked?: boolean };
      onRowSelect(item, actualIndex, eventWithChecked);
      return;
    }
    
    // Fallback: handle selection directly if onRowSelect is not provided
    const newSelectedItems = checked
      ? [...activeSelectedItems, item]
      : activeSelectedItems.filter(i => keyExtractor(i) !== keyExtractor(item));
    
    setLocalSelectedItems(newSelectedItems);
    if (onSelectionChange) {
      onSelectionChange(newSelectedItems);
    }
  };

  const isItemSelected = (item: any) => {
    return activeSelectedItems.some(i => keyExtractor(i) === keyExtractor(item));
  };

  const handlePageSizeChange = (newPageSize: string) => {
    const size = parseInt(newPageSize, 10);
    
    if (isPaginationControlled && onPageSizeChange) {
      onPageSizeChange(size);
      return;
    }
    
    // Update local pagination
    const totalPages = Math.ceil(filteredData.length / size);
    
    setLocalPagination(prev => {
      // Calculate the new current page to keep the same items visible if possible
      const firstItemIndex = (prev.currentPage - 1) * prev.pageSize;
      const newPage = Math.floor(firstItemIndex / size) + 1;
      
      return {
        ...prev,
        pageSize: size,
        currentPage: Math.min(newPage, totalPages),
        totalPages,
        hasNextPage: Math.min(newPage, totalPages) < totalPages,
        hasPrevPage: Math.min(newPage, totalPages) > 1
      };
    });
  };

  // Expose handlePageSizeChange through ref
  React.useImperativeHandle(ref, () => ({
    handlePageSizeChange
  }));

  const handleLocalPageChange = (page: number) => {
    if (isPaginationControlled && onPageChange) {
      onPageChange(page);
      return;
    }
    
    setLocalPagination(prev => ({
      ...prev,
      currentPage: page,
      hasNextPage: page < prev.totalPages,
      hasPrevPage: page > 1
    }));
  };

  // Get current page data
  const paginatedData = useMemo(() => {
    if (isPaginationControlled) {
      return filteredData; // Assume data is already paginated by the parent
    }
    
    const startIndex = (activePagination.currentPage - 1) * activePagination.pageSize;
    const endIndex = startIndex + activePagination.pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, activePagination, isPaginationControlled]);

  const renderPageNumbers = () => {
    const pages = [];
    const { currentPage, totalPages } = activePagination;
    
    // Always show first page
    pages.push(
      <PaginationItem key="page-1">
        <PaginationLink 
          isActive={currentPage === 1} 
          onClick={() => handleLocalPageChange(1)}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );
    
    // Show ellipsis if needed
    if (currentPage > 3) {
      pages.push(
        <PaginationItem key="ellipsis-1">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Show pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i === 1 || i === totalPages) continue; // Skip first and last page as they're always shown
      pages.push(
        <PaginationItem key={`page-${i}`}>
          <PaginationLink 
            isActive={currentPage === i} 
            onClick={() => handleLocalPageChange(i)}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    // Show ellipsis if needed
    if (currentPage < totalPages - 2) {
      pages.push(
        <PaginationItem key="ellipsis-2">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Always show last page if there's more than one page
    if (totalPages > 1) {
      pages.push(
        <PaginationItem key={`page-${totalPages}`}>
          <PaginationLink 
            isActive={currentPage === totalPages} 
            onClick={() => handleLocalPageChange(totalPages)}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return pages;
  };

  return (
    <div className={cn("space-y-4", !embedded && "rounded-md border p-4", containerClassName)}>
      {/* Search bar, custom header, and bulk actions */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {renderHeader ? (
          renderHeader()
        ) : searchable && (
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              className="w-full pl-9"
              value={isSearchControlled ? (searchTerm ?? "") : localSearchTerm}
              onChange={(e) => {
                if (isSearchControlled) {
                  onSearchTermChange(e.target.value);
                } else {
                  setLocalSearchTerm(e.target.value);
                }
              }}
              onKeyDown={
                onSearchSubmit
                  ? (e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onSearchSubmit();
                      }
                    }
                  : undefined
              }
            />
          </div>
        )}
        {renderHeaderExtras && renderHeaderExtras()}
        
        {/* Page size selector */}
        {!hidePageSizeSelector && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select 
              value={String(activePagination.pageSize)} 
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder={activePagination.pageSize} />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(size => (
                  <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {renderBulkActions && activeSelectedItems.length > 0 && (
          <div className="flex items-center ml-auto">
            <p className="text-sm font-medium mr-2">
              {activeSelectedItems.length} item{activeSelectedItems.length !== 1 ? 's' : ''} selected
            </p>
            {renderBulkActions(activeSelectedItems)}
          </div>
        )}
      </div>
      
      {/* Table */}
      <div className={cn("rounded-md border", tableClassName)}>
        <Table>
          <TableHeader>
            <TableRow>
              {selectable && (
                <TableHead className="w-[50px] p-2">
                  <Checkbox
                    checked={paginatedData.length > 0 && activeSelectedItems.length >= paginatedData.length}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
              )}
              {columns.map(column => (
                <TableHead 
                  key={String(column.key)} 
                  className={cn(column.width && column.width, column.className)}
                >
                  <SortableHeaderCell
                    column={column}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <td 
                  colSpan={columns.length + (selectable ? 1 : 0)} 
                  className="text-center py-6 text-muted-foreground"
                >
                  {loadingText}
                </td>
              </TableRow>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((item, paginatedIndex) => {
                // Calculate the actual index in the full filtered data
                const actualIndex = filteredData.findIndex(i => keyExtractor(i) === keyExtractor(item));
                return (
                  <SortableTableRow
                    key={keyExtractor(item)}
                    item={item}
                    columns={columns}
                    selected={isItemSelected(item)}
                    onSelect={(checked, event) => handleSelectItem(item, checked, event)}
                    onClick={onRowSelect ? (event) => onRowSelect(item, actualIndex, event) : onRowClick ? () => onRowClick(item) : undefined}
                    keyExtractor={keyExtractor}
                  />
                );
              })
            ) : (
              <TableRow>
                <td 
                  colSpan={columns.length + (selectable ? 1 : 0)} 
                  className="text-center py-6 text-muted-foreground"
                >
                  {noDataText}
                </td>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Pagination */}
      {activePagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {Math.min((activePagination.currentPage - 1) * activePagination.pageSize + 1, activePagination.totalItems)} to {Math.min(activePagination.currentPage * activePagination.pageSize, activePagination.totalItems)} of {activePagination.totalItems} entries
          </div>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => activePagination.hasPrevPage && handleLocalPageChange(activePagination.currentPage - 1)}
                  className={!activePagination.hasPrevPage ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              
              {renderPageNumbers()}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => activePagination.hasNextPage && handleLocalPageChange(activePagination.currentPage + 1)}
                  className={!activePagination.hasNextPage ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
      
      {children}
    </div>
  );
});

PaginatedTable.displayName = 'PaginatedTable';

export default PaginatedTable;
